import { MEMBERSHIP_LEVELS, ORG_ROLES } from "@ondo/shared";

import { Tag } from "../lib/Tag.tsx";
import { Panel } from "./ProfilePanels.tsx";
import {
  MEMBERSHIP_LEVEL_LABELS,
  ORG_PRIVILEGE_CHOICES,
  ORG_ROLE_LABELS,
  PROFILE_COPY,
  SEAT_ABILITY_OPTIONS,
} from "./profile.constants.ts";
import type { ProfileController } from "./useProfileController.ts";
import type { BundleRow, InvitationRow, TeamRow } from "./profile.model.ts";

/**
 * Everyone with an active membership, and what each of them holds.
 *
 * NO EMAIL COLUMN, and this is where somebody will look for one. The prototype's
 * roster shows an address beside every name; ONDO holds none — an email lives in
 * `auth.users`, reachable only with the service role, and putting one here would
 * mean the API reading it on behalf of anyone with a membership. `OrgMember` says
 * the same thing at the type.
 *
 * ACCESS IS SHOWN TO EVERYONE, not only to admins. That is the point rather than
 * an oversight: a registry where only admins can see who holds admin is one where
 * nobody but an admin can notice that the wrong person does. The whole argument,
 * including what it discloses, is in the header of `ondo.org_members()`.
 *
 * Ended and suspended memberships are absent. A roster is who is here now; who
 * used to be here would publish somebody's departure to everyone who opens the
 * page, which is an audit question and not a team list.
 *
 * ENDING A SEAT IS TWO PRESSES. OND-91, and the control appears only where
 * `canEnd` says the server would allow it — an admin, on a seat that is not the
 * firm's last administrator. A reader who cannot end a seat is not shown a
 * disabled button: on this screen the absence is the answer, and the one case
 * worth explaining in words is your own row as the only admin, which gets the
 * sentence rather than a missing control.
 */
export function OrgTeam({
  rows,
  bundles,
  invitations,
  canSetRoles,
  controller,
}: {
  rows: TeamRow[];
  bundles: BundleRow[];
  invitations: InvitationRow[];
  canSetRoles: boolean;
  controller: ProfileController;
}) {
  return (
    <>
      <Panel title={PROFILE_COPY.teamHeading} note={PROFILE_COPY.teamNote}>
        <ul className="profile__rows">
          {rows.map((row) => (
            <li className="profile__row" key={row.ondoId}>
              <span className="profile__initials profile__initials--small" aria-hidden="true">
                {row.initials}
              </span>
              <div className="profile__row-body">
                <span className="profile__row-title">{row.name}</span>
                <span className="profile__row-meta">
                  {row.title ? `${row.title} · ` : ""}
                  {row.ondoId} · {PROFILE_COPY.teamJoined} {row.joined}
                </span>
              </div>
              {/* `quiet` for a viewer and `neutral` above it: seniority reads at
                  a glance without inventing a colour, and neither tone is a
                  judgement — a tag says what something IS. */}
              <Tag tone={row.level === "viewer" ? "quiet" : "neutral"}>{row.levelLabel}</Tag>
              {/* THE ROLE, BESIDE THE RUNG AND NOT INSTEAD OF IT. OND-61: the
                  bundle is what the product enforces and the rung is the coarse
                  mechanism underneath it, so a roster showing one without the
                  other would hide half of why somebody can do something.
                  `quiet` for a seat with no named role, because "No named role"
                  is a real answer rather than a warning. */}
              <Tag tone={row.role === null ? "quiet" : "neutral"}>{row.roleLabel}</Tag>
              {/* WHAT THE SEAT CAN ACTUALLY DO — the owner's "Show the
                  effective abilities". Resolved by the server after the floor
                  and all four layers, so this is what the database WILL do and
                  not what the role name suggests. */}
              <ul className="profile__abilities">
                {row.abilities.map((ability) => (
                  <li className="profile__ability" key={ability}>
                    {ability}
                  </li>
                ))}
                {row.abilities.length === 0 && (
                  <li className="profile__ability profile__ability--none">
                    {PROFILE_COPY.teamNoAbilities}
                  </li>
                )}
              </ul>
              {/* Marked only where the seat's abilities are not what its role
                  name would give. A customised seat is the product working as
                  the owner asked, so this names the fact and passes no
                  judgement — it is for an administrator auditing a roster. */}
              {row.isCustomised && <Tag tone="neutral">{PROFILE_COPY.teamCustomised}</Tag>}
              {row.canSetRole && <SeatRole row={row} controller={controller} />}
              {row.canEnd && <EndSeat row={row} controller={controller} />}
              {/* THE ONE ABSENCE WORTH EXPLAINING. A sole administrator's own
                  seat cannot be ended — nobody could administer the firm
                  afterwards, and there is no door in the product to repair it —
                  so they get the sentence rather than a control that is simply
                  not there. `level` narrows it to an admin: a viewer looking at
                  their own row is not being refused anything they could expect. */}
              {!row.canEnd && row.isYou && row.level === "admin" && (
                <p className="profile__seat-note">{PROFILE_COPY.teamEndLast}</p>
              )}
            </li>
          ))}
        </ul>
      </Panel>

      {/* WHAT EACH ROLE MEANS HERE, and the one place the product says out loud
          that these bundles are ONDO's starting defaults rather than its rules —
          the owner's "these are initial defaults chosen under Andrew's
          delegation and can be changed".

          Shown to the whole roster for `ondo.org_role_bundles()`'s stated
          reason, which is OND-91's argument one level up: a registry where only
          administrators can see who may sign is one where nobody but an
          administrator can notice that the wrong person may. Editable only
          where the server says so. */}
      <Panel title={PROFILE_COPY.teamRoleLegend} note={PROFILE_COPY.teamRoleLegendNote}>
        <ul className="profile__rows">
          {bundles.map((bundle) => (
            <li className="profile__row profile__row--bundle" key={bundle.role}>
              <div className="profile__row-body">
                <span className="profile__row-title">{bundle.label}</span>
                <ul className="profile__abilities">
                  {bundle.cells.map((cell) => (
                    <li
                      className={
                        cell.granted
                          ? "profile__ability"
                          : "profile__ability profile__ability--off"
                      }
                      key={cell.privilege}
                    >
                      {canSetRoles ? (
                        <label className="profile__ability-toggle">
                          <input
                            type="checkbox"
                            checked={cell.granted}
                            disabled={controller.busy}
                            onChange={() =>
                              controller.setBundleLine(
                                bundle.role,
                                cell.privilege,
                                !cell.granted,
                              )
                            }
                          />
                          {cell.label}
                        </label>
                      ) : (
                        cell.label
                      )}
                      {cell.isEdited && <Tag tone="neutral">{PROFILE_COPY.teamCustomised}</Tag>}
                    </li>
                  ))}
                </ul>
              </div>
            </li>
          ))}
        </ul>
        {!canSetRoles && <p className="profile__seat-note">{PROFILE_COPY.teamRoleNotAdmin}</p>}
      </Panel>

      {/* INVITING A COLLEAGUE — OND-24, and the panel that said this was not
          built until this ticket. Shown only where the server says the reader
          may administer staff, which is the same flag the role controls above
          read: on this screen the absence is the answer, and a disabled form
          would be a shape to fill in and be refused.

          THE LIST IS BESIDE THE FORM AND NOT BEHIND A TAB, because the one
          question an administrator has after pressing Invite is whether it
          arrived — and nothing else in the product can answer it. */}
      {canSetRoles && <InviteColleague invitations={invitations} controller={controller} />}
    </>
  );
}

/**
 * The invite form and what the firm has sent.
 *
 * NO ONDO-ID FIELD, which is the first thing somebody will try to add. An
 * invitation is addressed to an EMAIL and never to an id, and that is not a
 * convenience: `20261014092200` decision 3 keeps the door from resolving the
 * address at all, so a firm cannot use an invitation to discover whether
 * somebody is on ONDO. A field taking an ONDO id would have to resolve one, and
 * "no endpoint resolves a name to an ONDO id, and none may."
 *
 * THE ADDRESS IS SHOWN BACK on every row, and `OrgMember`'s "no email column"
 * rule is answered rather than broken: this list is behind the `staff_admin`
 * door and the roster is readable by every seat, so these addresses reach a
 * strictly narrower audience. It is also the only way a mistyped address can be
 * diagnosed from a screen.
 */
function InviteColleague({
  invitations,
  controller,
}: {
  invitations: InvitationRow[];
  controller: ProfileController;
}) {
  const draft = controller.inviteDraft;

  return (
    <Panel title={PROFILE_COPY.teamInviteHeading} note={PROFILE_COPY.teamInviteNote}>
      {draft ? (
        <form
          className="profile__form"
          onSubmit={(event) => {
            event.preventDefault();
            controller.sendInvite();
          }}
        >
          <label className="profile__label">
            <span>{PROFILE_COPY.inviteEmail}</span>
            <input
              className="profile__input"
              type="email"
              autoComplete="off"
              value={draft.email}
              disabled={controller.busy}
              onChange={(event) => controller.changeInvite({ email: event.target.value })}
            />
          </label>

          <label className="profile__label">
            <span>{PROFILE_COPY.inviteLevel}</span>
            <select
              className="profile__input"
              value={draft.level}
              disabled={controller.busy}
              onChange={(event) => controller.changeInvite({ level: event.target.value })}
            >
              {MEMBERSHIP_LEVELS.map((level) => (
                <option value={level} key={level}>
                  {MEMBERSHIP_LEVEL_LABELS[level]}
                </option>
              ))}
            </select>
          </label>
          {/* The one warning on this panel, and it is about admin. See
              `inviteLevelNote` for why the option has to exist at all. */}
          <p className="profile__seat-note">{PROFILE_COPY.inviteLevelNote}</p>

          <label className="profile__label">
            <span>{PROFILE_COPY.inviteTitle}</span>
            <input
              className="profile__input"
              type="text"
              value={draft.title}
              disabled={controller.busy}
              onChange={(event) => controller.changeInvite({ title: event.target.value })}
            />
          </label>
          <p className="profile__seat-note">{PROFILE_COPY.inviteTitleHint}</p>

          <div className="profile__form-actions">
            <button type="submit" disabled={controller.busy}>
              {controller.busy ? PROFILE_COPY.inviteSending : PROFILE_COPY.inviteSend}
            </button>
            <button type="button" disabled={controller.busy} onClick={controller.cancelInvite}>
              {PROFILE_COPY.inviteCancel}
            </button>
          </div>
        </form>
      ) : (
        <button type="button" disabled={controller.busy} onClick={controller.startInvite}>
          {PROFILE_COPY.inviteStart}
        </button>
      )}

      <h4 className="profile__invite-heading">{PROFILE_COPY.inviteSentHeading}</h4>
      {invitations.length === 0 ? (
        <p className="profile__seat-note">{PROFILE_COPY.inviteNone}</p>
      ) : (
        <ul className="profile__rows">
          {invitations.map((invitation) => (
            <li className="profile__row" key={invitation.invitationId}>
              <div className="profile__row-body">
                <span className="profile__row-title">{invitation.email}</span>
                <span className="profile__row-meta">
                  {invitation.title ? `${invitation.title} · ` : ""}
                  {PROFILE_COPY.inviteBy} {invitation.invitedBy} · {invitation.sent}
                </span>
              </div>
              <Tag tone="quiet">{invitation.levelLabel}</Tag>
              {/* ONE SENTENCE ABOUT WHERE IT GOT TO, decided in the model. A
                  `quiet` tone throughout: an undelivered invitation is not a
                  fault to be flagged, because the screen cannot tell a slow
                  worker from a bad address. */}
              <Tag tone="quiet">{invitation.status}</Tag>
              {invitation.canWithdraw && (
                <button
                  type="button"
                  disabled={controller.busy}
                  onClick={() => controller.withdrawInvite(invitation.invitationId)}
                >
                  {PROFILE_COPY.inviteWithdraw}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/**
 * The first press arms; the second fires.
 *
 * An inline pair rather than a browser confirm, which is the viewing rooms' and
 * the catalogue declaration's pattern — a one-way act gets a second press naming
 * what cannot be undone, in the page's own words. Ending a seat is not quite
 * one-way (ONDO can restore it with the master key) but it is one-way from
 * inside the product, which is the same thing to the person pressing.
 *
 * THE WARNING IS SHOWN WHILE ARMED, not before. A sentence about closing the
 * collector list beside every row on the roster would be six warnings on a
 * screen where nothing has happened; beside the armed row it is what the next
 * press does.
 */
function EndSeat({
  row,
  controller,
}: {
  row: TeamRow;
  controller: ProfileController;
}) {
  const armed = controller.armedSeat === row.membershipId;

  if (!armed) {
    return (
      <button
        type="button"
        className="profile__seat-end"
        disabled={controller.busy}
        onClick={() => controller.armSeat(row.membershipId)}
      >
        {PROFILE_COPY.teamEnd}
      </button>
    );
  }

  return (
    <div className="profile__seat-armed">
      <p className="profile__seat-warning">{PROFILE_COPY.teamEndWarning}</p>
      <div className="profile__seat-actions">
        <button
          type="button"
          className="profile__seat-confirm"
          disabled={controller.busy}
          onClick={() => controller.endSeat(row.membershipId)}
        >
          {PROFILE_COPY.teamEndArmed}
        </button>
        <button
          type="button"
          className="profile__seat-cancel"
          disabled={controller.busy}
          onClick={controller.cancelSeat}
        >
          {PROFILE_COPY.teamEndCancel}
        </button>
      </div>
    </div>
  );
}

/**
 * The role selector and the per-seat ability controls, on one row.
 *
 * TWO CONTROLS AND NOT ONE, because they are two acts on two different rows:
 * the selector says which bundle this seat FOLLOWS, and an ability control says
 * where this seat DEPARTS from it. Folding them together would have made "give
 * Sophie the collector list" and "make Sophie a Registrar" the same gesture,
 * and they are not — the first is an exception to a default and the second
 * replaces the default.
 *
 * EACH ABILITY IS A THREE-WAY CHOICE, which looks fussier than a checkbox and
 * is the reason this works at all: "Yes", "No", and "Follow the role", which
 * CLEARS the exception. Without the third option an administrator who pressed
 * anything could never get the seat back to "whatever a Registrar is" — the
 * state almost every seat should be in, and a one-way door into permanent
 * per-seat exceptions.
 *
 * WHAT IT CANNOT SHOW, stated rather than left to be noticed: the select's
 * value is the seat's RESOLVED answer, so a "Yes" that came from an exception
 * and a "Yes" that came from the bundle look the same here.
 * `ondo.org_members()` returns the resolution and not the exceptions behind it,
 * deliberately — the roster's question is what a seat can DO. The row's "Set
 * for this seat" tag is what tells an administrator the seat differs from its
 * role at all, and narrowing that to per-ability would mean widening the
 * roster's shape for a fact the roster is not about.
 *
 * Drawn only where `canSetRole` says the server would allow it, on `canEnd`'s
 * rule: this screen's absences are its answers, and a disabled control the
 * database would refuse teaches nobody anything.
 */
function SeatRole({
  row,
  controller,
}: {
  row: TeamRow;
  controller: ProfileController;
}) {
  return (
    <div className="profile__seat-role">
      <label className="profile__seat-role-label">
        {PROFILE_COPY.teamRoleSet}
        <select
          className="profile__seat-role-select"
          value={row.role ?? ""}
          disabled={controller.busy}
          onChange={(event) => controller.setSeatRole(row.membershipId, event.target.value)}
        >
          {/* The empty option is "no named role", which is a real choice and
              not a prompt: clearing a role returns the seat to its rung's
              defaults, and "Legal" is none of the four. */}
          <option value="">{PROFILE_COPY.teamNoRole}</option>
          {ORG_ROLES.map((role) => (
            <option value={role} key={role}>
              {ORG_ROLE_LABELS[role]}
            </option>
          ))}
        </select>
      </label>
      <ul className="profile__seat-abilities">
        {ORG_PRIVILEGE_CHOICES.map((choice) => (
          <li className="profile__seat-ability" key={choice.privilege}>
            <label className="profile__seat-ability-label">
              {choice.label}
              <select
                className="profile__seat-ability-select"
                value={row.privileges.includes(choice.privilege) ? "yes" : "no"}
                disabled={controller.busy}
                onChange={(event) =>
                  controller.setSeatAbility(
                    row.membershipId,
                    choice.privilege,
                    event.target.value,
                  )
                }
              >
                {SEAT_ABILITY_OPTIONS.map((option) => (
                  <option value={option.value} key={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}
